(() => {
  const ROOT_SELECTOR = '[data-dg-carousel]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function closestSlideIndex(slides, scrollLeft) {
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  }

  function setupCarousel(root) {
    if (root.dataset.dgInitialized === 'true') return;

    const viewport = root.querySelector('[data-dg-carousel-viewport]');
    const slides = Array.from(root.querySelectorAll('[data-dg-carousel-slide]'));
    const previous = root.querySelector('[data-dg-carousel-previous]');
    const next = root.querySelector('[data-dg-carousel-next]');
    const dots = Array.from(root.querySelectorAll('[data-dg-carousel-dot]'));

    if (!viewport || slides.length === 0) return;
    root.dataset.dgInitialized = 'true';

    const loop = root.dataset.dgLoop !== 'false';
    const step = Math.max(1, Number.parseInt(root.dataset.dgScrollStep || '1', 10));
    const autoDelay = Math.max(1000, Number.parseInt(root.dataset.dgAutoplayDelay || '0', 10));
    let autoplayTimer = null;
    let paused = false;
    let scrollFrame = null;

    const maxScroll = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const atBeginning = () => viewport.scrollLeft <= 4;
    const atEnd = () => viewport.scrollLeft >= maxScroll() - 4;

    function syncControls() {
      const index = closestSlideIndex(slides, viewport.scrollLeft);
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (!loop) {
        if (previous) previous.disabled = atBeginning();
        if (next) next.disabled = atEnd();
      }
    }

    function scrollToIndex(index) {
      const targetIndex = Math.min(Math.max(index, 0), slides.length - 1);
      viewport.scrollTo({ left: slides[targetIndex].offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    function move(direction) {
      const current = closestSlideIndex(slides, viewport.scrollLeft);
      const nextIndex = current + direction * step;

      if (loop && direction > 0 && (atEnd() || nextIndex >= slides.length)) {
        viewport.scrollTo({ left: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        return;
      }

      if (loop && direction < 0 && (atBeginning() || nextIndex < 0)) {
        viewport.scrollTo({ left: maxScroll(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        return;
      }

      scrollToIndex(nextIndex);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function startAutoplay() {
      if (!autoDelay || reducedMotion.matches || paused || autoplayTimer || slides.length < 2) return;
      autoplayTimer = window.setInterval(() => {
        if (!document.hidden && !paused) move(1);
      }, autoDelay);
    }

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => scrollToIndex(index));
    });

    viewport.addEventListener('scroll', () => {
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(syncControls);
    }, { passive: true });

    root.addEventListener('mouseenter', () => {
      paused = true;
      stopAutoplay();
    });
    root.addEventListener('mouseleave', () => {
      paused = false;
      startAutoplay();
    });
    root.addEventListener('focusin', () => {
      paused = true;
      stopAutoplay();
    });
    root.addEventListener('focusout', event => {
      if (!root.contains(event.relatedTarget)) {
        paused = false;
        startAutoplay();
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(syncControls);
      observer.observe(viewport);
    }

    syncControls();
    startAutoplay();
  }

  function setupTabs(root) {
    if (root.dataset.dgTabsInitialized === 'true') return;

    const tabs = Array.from(root.querySelectorAll('[data-dg-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-dg-tab-panel]'));
    if (tabs.length === 0 || panels.length === 0) return;
    root.dataset.dgTabsInitialized = 'true';

    function activate(index, shouldFocus = false) {
      const safeIndex = Math.min(Math.max(index, 0), tabs.length - 1);
      tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === safeIndex;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        if (selected) tab.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', inline: 'nearest', block: 'nearest' });
      });
      panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== safeIndex;
      });
      if (shouldFocus) tabs[safeIndex].focus();
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', event => {
        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex !== null) {
          event.preventDefault();
          activate(nextIndex, true);
        }
      });
    });

    const activeIndex = Math.max(0, tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true'));
    activate(activeIndex);
  }

  function initialize(scope = document) {
    const carouselRoots = scope.matches?.(ROOT_SELECTOR) ? [scope] : Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []);
    carouselRoots.forEach(setupCarousel);
    const tabRoots = scope.matches?.('[data-dg-tabs]') ? [scope] : Array.from(scope.querySelectorAll?.('[data-dg-tabs]') || []);
    tabRoots.forEach(setupTabs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  if (!window.__dgNativeCarouselEditorEventsBound) {
    window.__dgNativeCarouselEditorEventsBound = true;
    document.addEventListener('shopify:section:load', event => initialize(event.target));
  }
})();
